import { api_fetch } from "./client.tsx"

/** Mirrors the backend `PromptContextOptions` — which context sections feed the AI. */
export interface CustomWizContextOptions {
  backup_files: boolean
  user_files: boolean
  messages: boolean
  notes: boolean
  checklist: boolean
  wins_examples: boolean
}

export const DEFAULT_CONTEXT_OPTIONS: CustomWizContextOptions = {
  backup_files: true,
  user_files: true,
  messages: true,
  notes: true,
  checklist: true,
  wins_examples: true,
}

export type CustomWizMode = "per_deduction" | "combined"

// Mirrors the backend guardrails in server/src/agent/mod.rs.
export const CUSTOM_WIZ_MAX_PER_DEDUCTION = 25
export const CUSTOM_WIZ_MAX_COMBINED = 10
export const CUSTOM_WIZ_MAX_PROMPT_CHARS = 4000

export interface RunCustomRequest {
  deduction_ids: string[]
  prompt: string
  context_options: CustomWizContextOptions
  mode: CustomWizMode
  /** Per-click key so a resubmitted/retried request never runs the wizard twice. */
  idempotency_key: string
}

export interface RunCustomResultItem {
  deduction_id: string
  run_id: string | null
  success: boolean
  action_taken: string | null
  error: string | null
}

export interface RunCustomResponse {
  ran: number
  batch_id: string
  message: string
  results: RunCustomResultItem[]
}

/** Pure request-body builder (trims the prompt; keeps toggles/mode as given). */
export function build_run_custom_body(
  deduction_ids: string[],
  prompt: string,
  context_options: CustomWizContextOptions,
  mode: CustomWizMode,
  idempotency_key: string
): RunCustomRequest {
  return { deduction_ids, prompt: prompt.trim(), context_options, mode, idempotency_key }
}

/** Pure client-side validation matching the backend guardrails. Returns an error message or null. */
export function validate_custom_wiz_input(deduction_count: number, prompt: string, mode: CustomWizMode): string | null {
  if (deduction_count === 0) return "No deductions selected"
  if (prompt.trim() === "") return "Tell the wizard what to do first"
  if (prompt.trim().length > CUSTOM_WIZ_MAX_PROMPT_CHARS) return `Prompt is too long (max ${CUSTOM_WIZ_MAX_PROMPT_CHARS} characters)`
  if (mode === "per_deduction" && deduction_count > CUSTOM_WIZ_MAX_PER_DEDUCTION) {
    return `Too many deductions selected (max ${CUSTOM_WIZ_MAX_PER_DEDUCTION})`
  }
  if (mode === "combined" && deduction_count > CUSTOM_WIZ_MAX_COMBINED) {
    return `Too many deductions for a combined run (max ${CUSTOM_WIZ_MAX_COMBINED})`
  }
  return null
}

export async function run_custom_wiz(body: RunCustomRequest) {
  return api_fetch<RunCustomResponse>("/agent/run-custom", { body })
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("build_run_custom_body", () => {
    it("passes ids/mode/key through and trims the prompt", () => {
      const body = build_run_custom_body(["a", "b"], "  do the thing  ", DEFAULT_CONTEXT_OPTIONS, "combined", "key-123")
      expect(body.deduction_ids).toEqual(["a", "b"])
      expect(body.prompt).toBe("do the thing")
      expect(body.mode).toBe("combined")
      expect(body.context_options.backup_files).toBe(true)
      expect(body.idempotency_key).toBe("key-123")
    })
  })

  describe("validate_custom_wiz_input", () => {
    it("requires a selection and a prompt", () => {
      expect(validate_custom_wiz_input(0, "x", "per_deduction")).not.toBeNull()
      expect(validate_custom_wiz_input(1, "   ", "per_deduction")).not.toBeNull()
      expect(validate_custom_wiz_input(1, "x", "per_deduction")).toBeNull()
    })
    it("enforces per-mode caps", () => {
      expect(validate_custom_wiz_input(CUSTOM_WIZ_MAX_PER_DEDUCTION, "x", "per_deduction")).toBeNull()
      expect(validate_custom_wiz_input(CUSTOM_WIZ_MAX_PER_DEDUCTION + 1, "x", "per_deduction")).not.toBeNull()
      expect(validate_custom_wiz_input(CUSTOM_WIZ_MAX_COMBINED, "x", "combined")).toBeNull()
      expect(validate_custom_wiz_input(CUSTOM_WIZ_MAX_COMBINED + 1, "x", "combined")).not.toBeNull()
    })
    it("enforces prompt length", () => {
      expect(validate_custom_wiz_input(1, "x".repeat(CUSTOM_WIZ_MAX_PROMPT_CHARS + 1), "per_deduction")).not.toBeNull()
    })
  })
}
